import { useState } from 'react'

import {
	Button, // CircularProgress,
	Paper,
	TextField,
	Typography,
} from '@mui/material'
import { Box } from '@mui/system'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'
import { brainstormText } from 'content/brainstormText'

import FacilitationHeader from 'components/FacilitationHeader'
import GeneratedExampleDropdown from 'components/GeneratedExampleDropdown'
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner'
import Section2Title from 'components/Section2Title'
import UserEducationModal from 'components/UserEducationModal'

import { OppositeDayPromptResponse } from '../../../../models/ideaModels'
import { styles } from './Section2.styles'

export const TEST_ID = 'opposite-day-section2'

export type Props = {
	loading: boolean
	oppositeDayPromptResponse?: OppositeDayPromptResponse
	prompt: string // do we need this?
	reloadIdeas: (newPrompt?: string) => Promise<boolean>
}

const buildResponseItem = (solution: string) => {
	if (!solution || solution.trim() === '') {
		return (
			<Box sx={styles.exampleIdeasBox}>
				<Typography variant="body1" style={styles.listItem}>
					No ideas generated yet. Click the refresh button to generate
					ideas.
				</Typography>
			</Box>
		)
	}

	try {
		const stringSol = JSON.parse(solution)
		return (
			<Box sx={styles.exampleIdeasBox}>
				<ol data-testid="gen-ai-response-list">
					{stringSol.map((el: string, i: number) => (
						<Typography
							fontSize={16}
							component="li"
							key={`ai-solution-${i}`}
							variant="body1"
							style={styles.listItem}
						>
							{el}
						</Typography>
					))}
				</ol>
			</Box>
		)
	} catch (error) {
		return (
			<Box sx={styles.exampleIdeasBox}>
				<Typography variant="body1" style={styles.listItem}>
					Error loading ideas. Please try refreshing.
				</Typography>
			</Box>
		)
	}
}

const Section2 = (props: Props) => {
	const [editPromptMode, setEditPromptMode] = useState(false)
	const [isGenAiLoading, setIsGenAiLoading] = useState(false)
	const [showAIExampleIdea, setShowAIExampleIdea] = useState(false)

	const onClickRefresh = () => {
		setIsGenAiLoading(true)
		// Call without parameter for refresh (will generate new prompt)
		props.reloadIdeas()
		setTimeout(() => {
			setIsGenAiLoading(false)
		}, 1000)
	}

	const onClickSaveEditPrompt = async () => {
		const textareaPrompt = document.getElementsByTagName('textarea')

		if (textareaPrompt && textareaPrompt.length > 0) {
			const newPromptValue = textareaPrompt[0].value
			const proceeded = await props.reloadIdeas(newPromptValue)

			if (proceeded) {
				setEditPromptMode(false)
			}
		}
	}

	const handleCancelButtonClick = () => {
		setEditPromptMode(false)
	}

	return (
		<>
			<Section2Title />
			<UserEducationModal />
			<Box style={styles.mainBox}>
				<Box sx={styles.sideBar} />
				<Box sx={styles.typographyBox}>
					<Typography
						sx={styles.typography}
						data-testid={`${TEST_ID}-user-directions-verbiage`}
					>
						Consider these intentionally terrible ideas and how the
						opposite of - or perhaps an iteration of - these
						concepts could lead to novelty.
					</Typography>
					<FacilitationHeader
						onRefresh={onClickRefresh}
						onEdit={
							editPromptMode
								? undefined
								: () => setEditPromptMode(true)
						}
						isLoading={props.loading || isGenAiLoading}
						refreshTestId={`${TEST_ID}__refresh-button`}
						editTestId={`${TEST_ID}-edit-prompt`}
					/>
					{editPromptMode ? (
						<>
							<TextField
								data-testid={`${TEST_ID}__edit-company-disruption`}
								defaultValue={
									props.oppositeDayPromptResponse?.data
										?.facilitationTechnique ||
									'Generate Scenario Above'
								}
								multiline
								fullWidth
								inputRef={(input) => input?.focus()}
							/>

							<Box sx={styles.saveButtonBox}>
								<Button
									color="secondary"
									variant="contained"
									data-testid={`${TEST_ID}__edit-save-button`}
									onClick={async () => {
										setIsGenAiLoading(true)
										await onClickSaveEditPrompt()
										setIsGenAiLoading(false)
									}}
								>
									<Typography>SAVE CHANGES</Typography>
								</Button>
								<Button
									variant="text"
									color="secondary"
									data-testid={`${TEST_ID}__edit-cancel-button`}
									onClick={() => handleCancelButtonClick()}
								>
									<Typography>CANCEL</Typography>
								</Button>
							</Box>
						</>
					) : (
						<>
							{isGenAiLoading || props.loading ? (
								<LoadingSpinner />
							) : (
								<Paper
									elevation={0}
									sx={styles.paper}
									data-testid={`${TEST_ID}__company-disruption`}
								>
									<Typography
										sx={{
											...styles.typography,
											...styles.techniqueBox,
										}}
									>
										{props.oppositeDayPromptResponse?.data
											?.facilitationTechnique ||
											'Generate Scenario Above'}
									</Typography>
								</Paper>
							)}
						</>
					)}
					<Typography
						sx={styles.typography}
						data-testid={`${TEST_ID}-genai-idea-dropdown`}
					>
						{brainstormText.oppositeDay.ideasDropdown}
					</Typography>
					<GeneratedExampleDropdown
						gameTypeEnum={GAME_TYPE.OPPOSITE_DAY}
						headerText={''}
						isLoading={isGenAiLoading}
						customResponse={buildResponseItem(
							props.oppositeDayPromptResponse?.data
								?.exampleIdeas ?? ''
						)}
						showAIExampleIdea={showAIExampleIdea}
						setShowAIExampleIdea={setShowAIExampleIdea}
					/>
				</Box>
			</Box>
		</>
	)
}

export default Section2
